import { useEffect, useState } from 'react';
import MetricCard from '@dashboard/shared/MetricCard';
import RevenueChart from '@dashboard/shared/RevenueChart';
import DateRangeSelector, { getFilterDates } from '@dashboard/shared/DateRangeSelector';
import type { DateFilter } from '@dashboard/shared/DateRangeSelector';
import SolutionCards from './SolutionCards';
import RecentOrdersTable from './RecentOrdersTable';
import AttentionAlert from './AttentionAlert';
import SparklineCard from './SparklineCard';
import QuickActions from './QuickActions';
import WelcomeCard from './WelcomeCard';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';
import type { Order, Profile, Merchant } from '@shared/types';

function isCryptoOrder(o: Order) {
  return o.provider === 'crypto' || o.payment_provider === 'crypto';
}

function isStripeOrder(o: Order) {
  return o.provider === 'stripe' || o.payment_provider === 'stripe';
}

function isSquareOrder(o: Order) {
  return o.provider === 'square' || o.payment_provider === 'square';
}

function getGreeting(timezone: string, name: string): string {
  let hour: number;
  try {
    const parts = new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone: timezone }).formatToParts(new Date());
    hour = Number(parts.find((p) => p.type === 'hour')?.value ?? new Date().getHours());
  } catch {
    hour = new Date().getHours();
  }
  if (hour < 12) return `Good morning, ${name}`;
  if (hour < 18) return `Good afternoon, ${name}`;
  return `Good evening, ${name}`;
}

export default function HomeDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [merchant, setMerchant] = useState<Merchant | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<DateFilter>({ range: '30d' });
  const [chartMode, setChartMode] = useState<'revenue' | 'orders'>('revenue');

  useEffect(() => {
    async function load() {
      const { start, end } = getFilterDates(dateFilter);
      let query = supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);

      if (start) query = query.gte('created_at', start);
      if (end) query = query.lte('created_at', end);

      const [ordersRes, profileRes, merchantRes] = await Promise.all([
        query,
        user
          ? supabase.from('profiles').select('*').eq('id', user.id).maybeSingle()
          : Promise.resolve({ data: null }),
        user
          ? supabase.from('merchants').select('*').eq('id', user.id).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      if (ordersRes.data) setOrders(ordersRes.data as unknown as Order[]);
      if (profileRes.data) setProfile(profileRes.data as unknown as Profile);
      if (merchantRes.data) setMerchant(merchantRes.data as unknown as Merchant);
      setLoading(false);
    }
    load();

    const channel = supabase
      .channel('home-orders-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, (payload) => {
        const incoming = payload.new as unknown as Order;
        if (payload.eventType === 'INSERT') {
          setOrders((prev) => [incoming, ...prev].slice(0, 500));
        } else if (payload.eventType === 'UPDATE') {
          setOrders((prev) =>
            prev.map((o) => (o.id === incoming.id ? incoming : o))
          );
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, dateFilter]);

  const cryptoOrders = orders.filter(isCryptoOrder);
  const stripeOrders = orders.filter(isStripeOrder);
  const squareOrders = orders.filter(isSquareOrder);

  const todayOrders = orders.filter(
    (o) => new Date(o.created_at).toDateString() === new Date().toDateString()
  );
  const todayCrypto = todayOrders.filter(isCryptoOrder);
  const todayStripe = todayOrders.filter(isStripeOrder);
  const todaySquare = todayOrders.filter(isSquareOrder);

  const todayRevenue = todayOrders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toDateString();
  const yesterdayRevenue = orders
    .filter((o) => o.status === 'completed' && new Date(o.created_at).toDateString() === yesterdayStr)
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);

  const revenueTrend = yesterdayRevenue > 0
    ? {
        value: `${Math.abs(((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100).toFixed(0)}% vs yesterday`,
        positive: todayRevenue >= yesterdayRevenue,
      }
    : null;

  const completedCount = todayOrders.filter((o) => o.status === 'completed').length;
  const successRate = todayOrders.length > 0
    ? ((completedCount / todayOrders.length) * 100).toFixed(0)
    : '0';

  const cryptoRevenue = cryptoOrders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);
  const stripeRevenue = stripeOrders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);
  const squareRevenue = squareOrders
    .filter((o) => o.status === 'completed')
    .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0);

  const cryptoPending = cryptoOrders.filter((o) => o.status === 'pending' || o.status === 'payment_initiated').length;
  const stripePending = stripeOrders.filter((o) => o.status === 'pending' || o.status === 'payment_initiated').length;
  const squarePending = squareOrders.filter((o) => o.status === 'pending' || o.status === 'payment_initiated').length;

  const last7Days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    return d.toDateString();
  });
  const weeklySparkline = last7Days.map((dayStr) =>
    orders
      .filter((o) => o.status === 'completed' && new Date(o.created_at).toDateString() === dayStr)
      .reduce((sum, o) => sum + (o.verified_amount || o.expected_amount), 0),
  );

  const chartBuckets: Record<string, { revenue: number; count: number }> = {};
  orders
    .filter((o) => o.status === 'completed')
    .forEach((o) => {
      const date = new Date(o.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      if (!chartBuckets[date]) chartBuckets[date] = { revenue: 0, count: 0 };
      chartBuckets[date].revenue += o.verified_amount || o.expected_amount;
      chartBuckets[date].count += 1;
    });

  const chartDataArray = Object.entries(chartBuckets)
    .map(([date, d]) => ({ date, revenue: chartMode === 'revenue' ? d.revenue : d.count }))
    .reverse();

  const stripeAccountId = merchant?.stripe_account_id || profile?.stripe_account_id;
  const stripeStatus = merchant?.stripe_onboarded
    ? 'Active'
    : stripeAccountId
      ? 'Pending'
      : 'Not Connected';

  const squareStatus = merchant?.square_onboarded
    ? 'Active'
    : merchant?.square_merchant_id
      ? 'Pending'
      : 'Not Connected';

  const timezone = (merchant?.settings as Record<string, unknown> | undefined)?.timezone as string || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const displayName = profile?.business_name || merchant?.business_name || user?.email?.split('@')[0] || 'Merchant';
  const greeting = getGreeting(timezone, displayName);

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-sans text-lg font-medium" style={{ color: 'var(--dash-text-primary)' }}>{greeting}</h1>
        <DateRangeSelector value={dateFilter} onChange={setDateFilter} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <MetricCard label="Today's Revenue" value={`$${todayRevenue.toFixed(2)}`} trend={revenueTrend} />
        <MetricCard label="Today's Orders" value={String(todayOrders.length)} />
        <MetricCard label="Success Rate" value={`${successRate}%`} />
        <SparklineCard
          label="This Week"
          value={`$${weeklySparkline.reduce((a, b) => a + b, 0).toFixed(2)}`}
          data={weeklySparkline}
        />
      </div>

      <AttentionAlert orders={orders} />

      <SolutionCards
        cryptoRevenue={cryptoRevenue}
        stripeRevenue={stripeRevenue}
        squareRevenue={squareRevenue}
        todayCryptoCount={todayCrypto.length}
        todayStripeCount={todayStripe.length}
        todaySquareCount={todaySquare.length}
        cryptoOrdersCount={cryptoOrders.length}
        stripeOrdersCount={stripeOrders.length}
        squareOrdersCount={squareOrders.length}
        stripeStatus={stripeStatus}
        squareStatus={squareStatus}
        cryptoPendingCount={cryptoPending}
        stripePendingCount={stripePending}
        squarePendingCount={squarePending}
      />

      <QuickActions />

      {orders.length === 0 && !loading ? (
        <WelcomeCard />
      ) : (
        <>
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>
                {chartMode === 'revenue' ? 'Revenue' : 'Order Count'}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setChartMode('revenue')}
                  className="px-2 py-1 rounded font-mono text-[10px] transition-colors"
                  style={{
                    border: `1px solid ${chartMode === 'revenue' ? 'var(--dash-active-border)' : 'var(--dash-border)'}`,
                    background: chartMode === 'revenue' ? 'var(--dash-active-bg)' : undefined,
                    color: chartMode === 'revenue' ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
                  }}
                >
                  Revenue
                </button>
                <button
                  onClick={() => setChartMode('orders')}
                  className="px-2 py-1 rounded font-mono text-[10px] transition-colors"
                  style={{
                    border: `1px solid ${chartMode === 'orders' ? 'var(--dash-active-border)' : 'var(--dash-border)'}`,
                    background: chartMode === 'orders' ? 'var(--dash-active-bg)' : undefined,
                    color: chartMode === 'orders' ? 'var(--dash-text-primary)' : 'var(--dash-text-muted)',
                  }}
                >
                  Orders
                </button>
              </div>
            </div>
            <RevenueChart data={chartDataArray} />
          </div>

          <RecentOrdersTable orders={orders.slice(0, 10)} loading={loading} />
        </>
      )}
    </div>
  );
}
